/**
 * Visit Notes Service Implementation (Core Layer)
 *
 * Business logic for visit notes management
 */

import type {
  CustomAssessmentCategory,
  IVisitNote,
  IVisitNotesService,
  IVisitNotesRepository,
  ICreateVisitNoteRequest,
  IUpdateVisitNoteRequest,
  ICreateVisitNoteResponse,
  IUpdateVisitNoteResponse,
  IGetAllVisitNotesResponse,
  IGetVisitNoteProvidersResponse,
  ISummarizeNarrativeRequest,
  ISummarizeNarrativeResponse,
  ISendEfaxRequest,
  ISendEfaxResponse,
  IDownloadPartnershipSummaryDocxRequest,
  IDownloadPartnershipSummaryDocxResponse,
  IGetSidePeekProfileResponse,
  IGenerateSidePeekProfileResponse,
  IUpdateSidePeekProfileResponse,
  IGetSafetyNoteResponse,
  IUpdateSafetyNoteResponse,
  IGetCodeStatusResponse,
  IUpdateCodeStatusResponse,
  CodeStatusValue,
  IGetPatientContactsResponse,
  IUpdatePatientContactsResponse,
  IUpdatePatientContactsRequest,
  IGetPatientExtraInfoResponse,
  IGetPatientPrimaryLanguageResponse,
  IUpdatePatientPrimaryLanguageResponse,
  IUpdatePatientTimezoneResponse,
  IGetNextVisitPlanResponse,
  IUpdateNextVisitPlanResponse,
  INextVisitPlan,
  IGetPatientSummaryResponse,
  IGetPatientSummaryGenerationStatusResponse,
  IGeneratePatientSummaryResponse,
  IGetNoteLlmOutputResponse,
  IClearManualEditsHistoryResponse,
  IPatientWithNotes,
  VisitNoteConsentType,
  IGetConsentTemplateMetadataResponse,
  IGetHhRpmConsentDraftMetadataResponse,
  IGetConsentTemplatePageResponse,
  IHhRpmConsentRequest,
  IHhRpmPatientConsentRequest,
  ISubmitHhRpmConsentResponse,
  IGetPatientConsentHistoryResponse,
  IGetClinicianAcuityCatalogResponse,
  IClinicianAssessmentUpsert,
  IPutClinicianAssessmentResponse,
  IRefreshPastContextPrechartResponse,
  ICarePlanPrechartResponse,
  IUpdateCarePlanPrechartRequest,
  IDeleteCarePlanPrechartRequest,
  IGetPatientCaregiversResponse,
  IPatientCaregiverResponse,
  IDeletePatientCaregiverResponse,
  ICreatePatientCaregiverRequest,
  IUpdatePatientCaregiverRequest,
  IPatientPrimaryCareProviderResponse,
  ICreatePatientPrimaryCareProviderRequest,
  IUpdatePatientPrimaryCareProviderRequest,
  IGetScheduledVisitAssignmentsResponse,
  IPatientSpecialistsResponse,
  IPatientSpecialistResponse,
  IDeletePatientSpecialistResponse,
  ICreatePatientSpecialistRequest,
  IUpdatePatientSpecialistRequest,
  InsuranceCardSide,
  IPatientInsuranceCardResponse,
  ICreateCustomAssessmentTemplateRequest,
  IDeleteCustomAssessmentFormResponse,
  ICustomAssessmentFormResponse,
  ICustomAssessmentFormsEnvelope,
  ICustomAssessmentResult,
  ICustomAssessmentTemplate,
  IPutCustomAssessmentFormRequest,
  IUpdateCustomAssessmentTemplateRequest,
} from "../ports/driver/VisitNotesService";
import { normalizeVisitNotesPatientRow } from "../../../util/normalizeVisitNotesPatientName";
import {
  buildRunningPatientSummary,
  isPatientSummaryStatusLikeRunning,
} from "../../../util/patientSummaryStatus";

function mapVisitNotesListData(
  data: NonNullable<IGetAllVisitNotesResponse["data"]>
): NonNullable<IGetAllVisitNotesResponse["data"]> {
  return {
    ...data,
    patients: (data.patients ?? []).map((p) =>
      normalizeVisitNotesPatientRow(p as IPatientWithNotes)
    ),
  };
}

const getMutationErrorMessage = (data: unknown, fallback: string): string => {
  if (!data || typeof data !== "object") return fallback;
  const record = data as Record<string, unknown>;
  if (typeof record.detail === "string") return record.detail;
  if (
    record.detail &&
    typeof record.detail === "object" &&
    typeof (record.detail as Record<string, unknown>).message === "string"
  ) {
    return (record.detail as Record<string, unknown>).message as string;
  }
  if (typeof record.message === "string") return record.message;
  return fallback;
};

const assertVisitNoteMutationSucceeded: (
  data: unknown,
  fallback: string
) => asserts data is IVisitNote = (data, fallback) => {
  if (
    !data ||
    typeof data !== "object" ||
    typeof (data as { id?: unknown }).id !== "number"
  ) {
    throw new Error(getMutationErrorMessage(data, fallback));
  }
};

export const VisitNotesServiceImplFactory = ({
  visitNotesRepository,
}: {
  visitNotesRepository: IVisitNotesRepository;
}): IVisitNotesService => {
  /**
   * Create a new visit note
   */
  const createVisitNote = async (
    request: ICreateVisitNoteRequest
  ): Promise<ICreateVisitNoteResponse> => {
    try {
      const data = await visitNotesRepository.createVisitNoteAPI(request);
      assertVisitNoteMutationSucceeded(data, "Failed to create visit note");

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to create visit note:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to create visit note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update an existing visit note
   */
  const updateVisitNote = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IUpdateVisitNoteRequest
  ): Promise<IUpdateVisitNoteResponse> => {
    try {
      const data = await visitNotesRepository.updateVisitNoteAPI(
        clinicId,
        patientId,
        noteId,
        request
      );
      assertVisitNoteMutationSucceeded(data, "Failed to update visit note");

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update visit note:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update visit note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get all visit notes for a clinic (current-clinic scoping; mobile clients).
   */
  const getAllVisitNotes = async (
    clinicId: number,
    params?: {
      monthOffset?: number;
      daysBack?: number;
      daysForward?: number;
      providerEmail?: string;
      providerName?: string;
      includeEmpty?: boolean;
      skipHealthscribe?: boolean;
      circadia?: boolean;
    }
  ): Promise<IGetAllVisitNotesResponse> => {
    try {
      const data = await visitNotesRepository.getAllVisitNotesAPI(
        clinicId,
        params
      );

      return {
        success: true,
        data: data ? mapVisitNotesListData(data) : data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get all visit notes:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get all visit notes",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get all visit notes for a clinic (admission-scoped variant for the web
   * list view — surfaces transferred patients' historical notes under the
   * old clinic). Same params/response shape as getAllVisitNotes.
   */
  const getAllVisitNotesFull = async (
    clinicId: number,
    params?: {
      monthOffset?: number;
      daysBack?: number;
      daysForward?: number;
      providerEmail?: string;
      providerName?: string;
      includeEmpty?: boolean;
      skipHealthscribe?: boolean;
      circadia?: boolean;
    }
  ): Promise<IGetAllVisitNotesResponse> => {
    try {
      const data = await visitNotesRepository.getAllVisitNotesFullAPI(
        clinicId,
        params
      );

      return {
        success: true,
        data: data ? mapVisitNotesListData(data) : data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get all visit notes (full):",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get all visit notes",
          code: error?.code,
        },
      };
    }
  };

  const getScheduledVisitAssignments = async (
    clinicId: number,
    daysForward?: number
  ): Promise<IGetScheduledVisitAssignmentsResponse> => {
    try {
      const data = await visitNotesRepository.getScheduledVisitAssignmentsAPI(
        clinicId,
        daysForward
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to load scheduled assignments",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get distinct providers for the visit notes provider filter dropdown
   */
  const getVisitNoteProviders = async (
    clinicId: number,
    params?: { circadia?: boolean }
  ): Promise<IGetVisitNoteProvidersResponse> => {
    try {
      const data = await visitNotesRepository.getVisitNoteProvidersAPI(
        clinicId,
        params
      );
      return { success: true, data };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get visit note providers:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get visit note providers",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Server-side search for visit notes (transcript search only)
   */
  const searchVisitNotes = async (
    clinicId: number,
    searchQuery?: string,
    limit?: number
  ): Promise<IGetAllVisitNotesResponse> => {
    try {
      const data = await visitNotesRepository.searchVisitNotesAPI(
        clinicId,
        searchQuery,
        limit
      );
      return {
        success: true,
        data: data ? mapVisitNotesListData(data) : data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to search visit notes:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to search visit notes",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get archived visit notes for a clinic
   */
  const getArchivedVisitNotes = async (
    clinicId: number,
    params?: { circadia?: boolean }
  ): Promise<IGetAllVisitNotesResponse> => {
    try {
      const data = await visitNotesRepository.getArchivedVisitNotesAPI(
        clinicId,
        params
      );

      return {
        success: true,
        data: data ? mapVisitNotesListData(data) : data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get archived visit notes:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get archived visit notes",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Restore an archived visit note
   */
  const restoreArchivedVisitNote = async (
    clinicId: number,
    ipvNoteId: number
  ): Promise<{ success: boolean; error?: { message: string } }> => {
    try {
      await visitNotesRepository.restoreArchivedVisitNoteAPI(
        clinicId,
        ipvNoteId
      );
      return { success: true };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to restore archived visit note:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to restore note",
        },
      };
    }
  };

  /**
   * Get a single visit note
   */
  const getVisitNote = async (
    noteId: number
  ): Promise<{ success: boolean; data?: any; error?: any }> => {
    try {
      const data = await visitNotesRepository.getVisitNoteAPI(noteId);

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to get visit note:", error);
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get visit note",
          code: error?.code,
        },
      };
    }
  };

  const getClinicianAcuityCatalog =
    async (): Promise<IGetClinicianAcuityCatalogResponse> => {
      try {
        const data = await visitNotesRepository.getClinicianAcuityCatalogAPI();
        return { success: true, data };
      } catch (error: any) {
        return {
          success: false,
          error: {
            message: error?.message || "Failed to load acuity catalog",
            code: error?.code,
          },
        };
      }
    };

  const putClinicianAssessment = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IClinicianAssessmentUpsert
  ): Promise<IPutClinicianAssessmentResponse> => {
    try {
      const data = await visitNotesRepository.putClinicianAssessmentAPI(
        clinicId,
        patientId,
        noteId,
        request
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to save acuity assessment",
          code: error?.code,
        },
      };
    }
  };

  const customAssessmentResult = async <T>(
    action: () => Promise<T>,
    fallback: string
  ): Promise<ICustomAssessmentResult<T>> => {
    try {
      return { success: true, data: await action() };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || fallback,
          code: error?.code,
        },
      };
    }
  };

  const listCustomAssessmentTemplates = (
    clinicId: number,
    category: CustomAssessmentCategory,
    includeArchived?: boolean
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate[]>> =>
    customAssessmentResult(
      () =>
        visitNotesRepository.listCustomAssessmentTemplatesAPI(
          clinicId,
          category,
          includeArchived
        ),
      "Failed to load assessment forms"
    );

  const getCustomAssessmentTemplate = (
    clinicId: number,
    templateId: number
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate>> =>
    customAssessmentResult(
      () =>
        visitNotesRepository.getCustomAssessmentTemplateAPI(
          clinicId,
          templateId
        ),
      "Failed to load assessment form"
    );

  const createCustomAssessmentTemplate = (
    clinicId: number,
    request: ICreateCustomAssessmentTemplateRequest
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate>> =>
    customAssessmentResult(
      () =>
        visitNotesRepository.createCustomAssessmentTemplateAPI(
          clinicId,
          request
        ),
      "Failed to create assessment form"
    );

  const updateCustomAssessmentTemplate = (
    clinicId: number,
    templateId: number,
    request: IUpdateCustomAssessmentTemplateRequest
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate>> =>
    customAssessmentResult(
      () =>
        visitNotesRepository.updateCustomAssessmentTemplateAPI(
          clinicId,
          templateId,
          request
        ),
      "Failed to update assessment form"
    );

  const archiveCustomAssessmentTemplate = (
    clinicId: number,
    templateId: number,
    baseRevision: number
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate>> =>
    customAssessmentResult(
      () =>
        visitNotesRepository.archiveCustomAssessmentTemplateAPI(
          clinicId,
          templateId,
          baseRevision
        ),
      "Failed to archive assessment form"
    );

  const publishCustomAssessmentTemplate = (
    clinicId: number,
    templateId: number,
    baseRevision: number
  ): Promise<ICustomAssessmentResult<ICustomAssessmentTemplate>> =>
    customAssessmentResult(
      () =>
        visitNotesRepository.publishCustomAssessmentTemplateAPI(
          clinicId,
          templateId,
          baseRevision
        ),
      "Failed to publish assessment form"
    );

  const getCustomAssessmentForms = (
    clinicId: number,
    patientId: number,
    noteId: number
  ): Promise<ICustomAssessmentResult<ICustomAssessmentFormsEnvelope>> =>
    customAssessmentResult(
      () =>
        visitNotesRepository.getCustomAssessmentFormsAPI(
          clinicId,
          patientId,
          noteId
        ),
      "Failed to load note assessments"
    );

  const putCustomAssessmentForm = (
    clinicId: number,
    patientId: number,
    noteId: number,
    templateId: number,
    request: IPutCustomAssessmentFormRequest
  ): Promise<ICustomAssessmentResult<ICustomAssessmentFormResponse>> =>
    customAssessmentResult(
      () =>
        visitNotesRepository.putCustomAssessmentFormAPI(
          clinicId,
          patientId,
          noteId,
          templateId,
          request
        ),
      "Failed to save note assessment"
    );

  const deleteCustomAssessmentForm = (
    clinicId: number,
    patientId: number,
    noteId: number,
    templateId: number,
    baseRevision: number
  ): Promise<ICustomAssessmentResult<IDeleteCustomAssessmentFormResponse>> =>
    customAssessmentResult(
      () =>
        visitNotesRepository.deleteCustomAssessmentFormAPI(
          clinicId,
          patientId,
          noteId,
          templateId,
          baseRevision
        ),
      "Failed to delete assessment draft"
    );

  const refreshPastContextPrechart = async (
    clinicId: number,
    patientId: number,
    noteId: number
  ): Promise<IRefreshPastContextPrechartResponse> => {
    try {
      const data = await visitNotesRepository.refreshPastContextPrechartAPI(
        clinicId,
        patientId,
        noteId
      );
      return { success: true, data };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to refresh past visit context:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to refresh prior visit context",
          code: error?.code,
        },
      };
    }
  };

  const carePlanPrechartError = (
    error: any,
    fallbackMessage: string
  ): ICarePlanPrechartResponse => ({
    success: false,
    error: {
      message: error?.message || fallbackMessage,
      code: error?.code,
    },
  });

  const getCarePlanPrechart = async (
    clinicId: number,
    patientId: number,
    noteId: number
  ): Promise<ICarePlanPrechartResponse> => {
    try {
      const data = await visitNotesRepository.getCarePlanPrechartAPI(
        clinicId,
        patientId,
        noteId
      );
      return { success: true, data };
    } catch (error: any) {
      return carePlanPrechartError(
        error,
        "Failed to load the prechart care plan"
      );
    }
  };

  const updateCarePlanPrechart = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IUpdateCarePlanPrechartRequest
  ): Promise<ICarePlanPrechartResponse> => {
    try {
      const data = await visitNotesRepository.updateCarePlanPrechartAPI(
        clinicId,
        patientId,
        noteId,
        request
      );
      return { success: true, data };
    } catch (error: any) {
      return carePlanPrechartError(
        error,
        "Failed to save the prechart care plan"
      );
    }
  };

  const refreshCarePlanPrechart = async (
    clinicId: number,
    patientId: number,
    noteId: number
  ): Promise<ICarePlanPrechartResponse> => {
    try {
      const data = await visitNotesRepository.refreshCarePlanPrechartAPI(
        clinicId,
        patientId,
        noteId
      );
      return { success: true, data };
    } catch (error: any) {
      return carePlanPrechartError(
        error,
        "Failed to refresh the prechart care plan"
      );
    }
  };

  const deleteCarePlanPrechart = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    request: IDeleteCarePlanPrechartRequest
  ): Promise<ICarePlanPrechartResponse> => {
    try {
      const data = await visitNotesRepository.deleteCarePlanPrechartAPI(
        clinicId,
        patientId,
        noteId,
        request
      );
      return { success: true, data };
    } catch (error: any) {
      return carePlanPrechartError(
        error,
        "Failed to delete the prechart care plan"
      );
    }
  };

  /**
   * Summarize a narrative for sharing
   */
  const summarizeNarrative = async (
    request: ISummarizeNarrativeRequest
  ): Promise<ISummarizeNarrativeResponse> => {
    try {
      const response = await visitNotesRepository.summarizeNarrativeAPI(
        request
      );

      return response;
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to summarize narrative:",
        error
      );
      return {
        success: false,
        error: error?.message || "Failed to summarize narrative",
      };
    }
  };

  /**
   * Send visit note PDF via eFax
   */
  const sendEfax = async (
    request: ISendEfaxRequest
  ): Promise<ISendEfaxResponse> => {
    try {
      const response = await visitNotesRepository.sendEfaxAPI(request);
      return response;
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to send eFax:", error);
      throw error;
    }
  };

  /**
   * Generate and download Compassus partnership summary DOCX.
   */
  const downloadPartnershipSummaryDocx = async (
    request: IDownloadPartnershipSummaryDocxRequest
  ): Promise<IDownloadPartnershipSummaryDocxResponse> => {
    try {
      return await visitNotesRepository.downloadPartnershipSummaryDocxAPI(
        request
      );
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to download partnership summary DOCX:",
        error
      );
      throw error;
    }
  };

  /**
   * Get Side Peek Profile for a patient
   */
  const getSidePeekProfile = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSidePeekProfileResponse> => {
    try {
      const result = await visitNotesRepository.getSidePeekProfileAPI(
        clinicId,
        patientId
      );

      const dobFromInfo =
        result.patientInfo?.dob != null &&
        String(result.patientInfo.dob).trim() !== ""
          ? String(result.patientInfo.dob).trim()
          : undefined;

      return {
        success: true,
        data: result.sidepeek_profile || undefined,
        patient_dob: result.patient_dob ?? dobFromInfo,
        patientInfo: result.patientInfo,
        hospiceDecline: result.hospice_decline ?? null,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get side peek profile:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get side peek profile",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Trigger Side Peek Profile generation for a patient
   */
  const generateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    section?: "diagnoses" | "medications" | "allergies",
    model?: string
  ): Promise<IGenerateSidePeekProfileResponse> => {
    try {
      const data = await visitNotesRepository.generateSidePeekProfileAPI(
        clinicId,
        patientId,
        noteId,
        section,
        model
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to generate side peek profile:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to generate side peek profile",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update Side Peek Profile for a patient (manual edits)
   */
  const updateSidePeekProfile = async (
    clinicId: number,
    patientId: number,
    updateData: {
      diagnoses?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        icd10_code?: string | null;
        is_primary?: boolean | null;
        onset_date?: string | null;
      }>;
      medications?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        status?: string | null;
      }>;
      allergies?: Array<{
        value: string;
        source: string | null;
        reference: string | null;
        note_id?: number | null;
        added_at?: string | null;
        icd10_code?: string | null;
        status?: string | null;
        allergen?: string | null;
        reaction?: string | null;
        severity?: string | null;
        onset_date?: string | null;
        is_primary?: boolean | null;
      }>;
    }
  ): Promise<IUpdateSidePeekProfileResponse> => {
    try {
      const data = await visitNotesRepository.updateSidePeekProfileAPI(
        clinicId,
        patientId,
        updateData
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update side peek profile:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update side peek profile",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get Safety Note for a patient
   */
  const getSafetyNote = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetSafetyNoteResponse> => {
    try {
      const data = await visitNotesRepository.getSafetyNoteAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to get safety note:", error);
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get safety note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update Safety Note for a patient
   */
  const updateSafetyNote = async (
    clinicId: number,
    patientId: number,
    content: string
  ): Promise<IUpdateSafetyNoteResponse> => {
    try {
      const data = await visitNotesRepository.updateSafetyNoteAPI(
        clinicId,
        patientId,
        content
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update safety note:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update safety note",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get Code Status for a patient
   */
  const getCodeStatus = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetCodeStatusResponse> => {
    try {
      const data = await visitNotesRepository.getCodeStatusAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error("[VisitNotesService] ❌ Failed to get code status:", error);
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get code status",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update Code Status for a patient
   */
  const updateCodeStatus = async (
    clinicId: number,
    patientId: number,
    status: CodeStatusValue,
    otherStatus?: string | null
  ): Promise<IUpdateCodeStatusResponse> => {
    try {
      const data = await visitNotesRepository.updateCodeStatusAPI(
        clinicId,
        patientId,
        status,
        otherStatus
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update code status:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update code status",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get Patient Contacts for a patient
   */
  const getPatientContacts = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientContactsResponse> => {
    try {
      const result = await visitNotesRepository.getPatientContactsAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        data: result.contacts,
        patientInfo: result.patientInfo,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get patient contacts:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get patient contacts",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Add or remove a patient contact
   */
  const updatePatientContacts = async (
    clinicId: number,
    patientId: number,
    request: IUpdatePatientContactsRequest
  ): Promise<IUpdatePatientContactsResponse> => {
    try {
      const data = await visitNotesRepository.updatePatientContactsAPI(
        clinicId,
        patientId,
        request
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update patient contacts:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update patient contacts",
          code: error?.code,
        },
      };
    }
  };

  const getPatientCaregivers = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientCaregiversResponse> => {
    try {
      const data = await visitNotesRepository.getPatientCaregiversAPI(
        clinicId,
        patientId
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to load caregivers",
          code: error?.code,
        },
      };
    }
  };

  const createPatientCaregiver = async (
    clinicId: number,
    patientId: number,
    request: ICreatePatientCaregiverRequest
  ): Promise<IPatientCaregiverResponse> => {
    try {
      const data = await visitNotesRepository.createPatientCaregiverAPI(
        clinicId,
        patientId,
        request
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to add caregiver",
          code: error?.code,
        },
      };
    }
  };

  const updatePatientCaregiver = async (
    clinicId: number,
    patientId: number,
    caregiverId: string,
    request: IUpdatePatientCaregiverRequest
  ): Promise<IPatientCaregiverResponse> => {
    try {
      const data = await visitNotesRepository.updatePatientCaregiverAPI(
        clinicId,
        patientId,
        caregiverId,
        request
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update caregiver",
          code: error?.code,
        },
      };
    }
  };

  const deletePatientCaregiver = async (
    clinicId: number,
    patientId: number,
    caregiverId: string
  ): Promise<IDeletePatientCaregiverResponse> => {
    try {
      await visitNotesRepository.deletePatientCaregiverAPI(
        clinicId,
        patientId,
        caregiverId
      );
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to delete caregiver",
          code: error?.code,
        },
      };
    }
  };

  const getPatientPrimaryCareProvider = async (
    clinicId: number,
    patientId: number
  ): Promise<IPatientPrimaryCareProviderResponse> => {
    try {
      const data = await visitNotesRepository.getPatientPrimaryCareProviderAPI(
        clinicId,
        patientId
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to load primary care provider",
          code: error?.code,
        },
      };
    }
  };

  const createPatientPrimaryCareProvider = async (
    clinicId: number,
    patientId: number,
    request: ICreatePatientPrimaryCareProviderRequest
  ): Promise<IPatientPrimaryCareProviderResponse> => {
    try {
      const data =
        await visitNotesRepository.createPatientPrimaryCareProviderAPI(
          clinicId,
          patientId,
          request
        );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to add primary care provider",
          code: error?.code,
        },
      };
    }
  };

  const updatePatientPrimaryCareProvider = async (
    clinicId: number,
    patientId: number,
    request: IUpdatePatientPrimaryCareProviderRequest
  ): Promise<IPatientPrimaryCareProviderResponse> => {
    try {
      const data =
        await visitNotesRepository.updatePatientPrimaryCareProviderAPI(
          clinicId,
          patientId,
          request
        );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update primary care provider",
          code: error?.code,
        },
      };
    }
  };

  const getPatientSpecialists = async (
    clinicId: number,
    patientId: number
  ): Promise<IPatientSpecialistsResponse> => {
    try {
      const data = await visitNotesRepository.getPatientSpecialistsAPI(
        clinicId,
        patientId
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to load specialists",
          code: error?.code,
        },
      };
    }
  };

  const createPatientSpecialist = async (
    clinicId: number,
    patientId: number,
    request: ICreatePatientSpecialistRequest
  ): Promise<IPatientSpecialistResponse> => {
    try {
      const data = await visitNotesRepository.createPatientSpecialistAPI(
        clinicId,
        patientId,
        request
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to add specialist",
          code: error?.code,
        },
      };
    }
  };

  const updatePatientSpecialist = async (
    clinicId: number,
    patientId: number,
    specialistId: number,
    request: IUpdatePatientSpecialistRequest
  ): Promise<IPatientSpecialistResponse> => {
    try {
      const data = await visitNotesRepository.updatePatientSpecialistAPI(
        clinicId,
        patientId,
        specialistId,
        request
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update specialist",
          code: error?.code,
        },
      };
    }
  };

  const deletePatientSpecialist = async (
    clinicId: number,
    patientId: number,
    specialistId: number
  ): Promise<IDeletePatientSpecialistResponse> => {
    try {
      await visitNotesRepository.deletePatientSpecialistAPI(
        clinicId,
        patientId,
        specialistId
      );
      return { success: true };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to delete specialist",
          code: error?.code,
        },
      };
    }
  };

  const getPatientInsuranceCard = async (
    clinicId: number,
    patientId: number
  ): Promise<IPatientInsuranceCardResponse> => {
    try {
      const data = await visitNotesRepository.getPatientInsuranceCardAPI(
        clinicId,
        patientId
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to load insurance card images",
          code: error?.code,
        },
      };
    }
  };

  const uploadPatientInsuranceCardImage = async (
    clinicId: number,
    patientId: number,
    side: InsuranceCardSide,
    file: File
  ): Promise<IPatientInsuranceCardResponse> => {
    try {
      await visitNotesRepository.uploadPatientInsuranceCardImageAPI(
        clinicId,
        patientId,
        side,
        file
      );
      const data = await visitNotesRepository.getPatientInsuranceCardAPI(
        clinicId,
        patientId
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || `Failed to upload insurance card ${side}`,
          code: error?.code,
        },
      };
    }
  };

  const deletePatientInsuranceCardImage = async (
    clinicId: number,
    patientId: number,
    side: InsuranceCardSide
  ): Promise<IPatientInsuranceCardResponse> => {
    try {
      await visitNotesRepository.deletePatientInsuranceCardImageAPI(
        clinicId,
        patientId,
        side
      );
      const data = await visitNotesRepository.getPatientInsuranceCardAPI(
        clinicId,
        patientId
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || `Failed to delete insurance card ${side}`,
          code: error?.code,
        },
      };
    }
  };

  const getPatientExtraInfo = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientExtraInfoResponse> => {
    try {
      const data = await visitNotesRepository.getPatientExtraInfoAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to get patient extra info:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get patient extra info",
          code: error?.code,
        },
      };
    }
  };

  const getPatientPrimaryLanguage = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientPrimaryLanguageResponse> => {
    const result = await getPatientExtraInfo(clinicId, patientId);
    if (!result.success) {
      return {
        success: false,
        error: result.error,
      };
    }
    return {
      success: true,
      data: {
        primary_language: result.data?.primary_language ?? null,
      },
    };
  };

  const updatePatientPrimaryLanguage = async (
    clinicId: number,
    patientId: number,
    primaryLanguage: string | null
  ): Promise<IUpdatePatientPrimaryLanguageResponse> => {
    try {
      const data = await visitNotesRepository.updatePatientPrimaryLanguageAPI(
        clinicId,
        patientId,
        primaryLanguage
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update patient primary language:",
        error
      );
      return {
        success: false,
        error: {
          message:
            error?.message || "Failed to update patient primary language",
          code: error?.code,
        },
      };
    }
  };

  const updatePatientTimezone = async (
    clinicId: number,
    patientId: number,
    patientTimezone: string | null
  ): Promise<IUpdatePatientTimezoneResponse> => {
    try {
      const data = await visitNotesRepository.updatePatientTimezoneAPI(
        clinicId,
        patientId,
        patientTimezone
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] ❌ Failed to update patient timezone:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update patient timezone",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get Next Visit Plan for a patient
   */
  const getNextVisitPlan = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetNextVisitPlanResponse> => {
    try {
      const data = await visitNotesRepository.getNextVisitPlanAPI(
        clinicId,
        patientId
      );

      return {
        success: true,
        ...(data !== null ? { data } : {}),
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to get next visit plan:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get next visit plan",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Update Next Visit Plan for a patient
   */
  const updateNextVisitPlan = async (
    clinicId: number,
    patientId: number,
    plan: INextVisitPlan["plan"]
  ): Promise<IUpdateNextVisitPlanResponse> => {
    try {
      const data = await visitNotesRepository.updateNextVisitPlanAPI(
        clinicId,
        patientId,
        plan
      );

      return {
        success: true,
        data,
      };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to update next visit plan:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to update next visit plan",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get the stored patient summary.
   * If the summary does not exist yet (exists: false), the caller should
   * trigger generatePatientSummary to create one on demand.
   */
  const getPatientSummary = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientSummaryResponse> => {
    try {
      const data = await visitNotesRepository.getPatientSummaryAPI(
        clinicId,
        patientId
      );
      return { success: true, data };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to get patient summary:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get patient summary",
          code: error?.code,
        },
      };
    }
  };

  const getPatientSummaryGenerationStatus = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetPatientSummaryGenerationStatusResponse> => {
    try {
      const data =
        await visitNotesRepository.getPatientSummaryGenerationStatusAPI(
          clinicId,
          patientId
        );
      return { success: true, data };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to get patient summary generation status:",
        error
      );
      return {
        success: false,
        error: {
          message:
            error?.message || "Failed to get patient summary generation status",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Trigger on-demand generation of the patient summary via the POST endpoint.
   */
  const generatePatientSummary = async (
    clinicId: number,
    patientId: number
  ): Promise<IGeneratePatientSummaryResponse> => {
    try {
      try {
        const status =
          await visitNotesRepository.getPatientSummaryGenerationStatusAPI(
            clinicId,
            patientId
          );
        if (isPatientSummaryStatusLikeRunning(status)) {
          return {
            success: true,
            data: buildRunningPatientSummary(status, patientId),
          };
        }
      } catch (statusError) {
        console.warn(
          "[VisitNotesService] Unable to check patient summary generation status before POST:",
          statusError
        );
      }

      const data = await visitNotesRepository.generatePatientSummaryAPI(
        clinicId,
        patientId
      );
      return { success: true, data };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to generate patient summary:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to generate patient summary",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Get latest LLM output for a note
   */
  const getNoteLlmOutput = async (
    noteId: number,
    clinicId: number
  ): Promise<IGetNoteLlmOutputResponse> => {
    try {
      const data = await visitNotesRepository.getNoteLlmOutputAPI(
        noteId,
        clinicId
      );
      return { success: true, data };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to get note LLM output:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to get LLM output",
          code: error?.code,
        },
      };
    }
  };

  /**
   * Clear manual edits history for a note
   */
  const clearManualEditsHistory = async (
    noteId: number,
    clinicId: number
  ): Promise<IClearManualEditsHistoryResponse> => {
    try {
      await visitNotesRepository.clearManualEditsHistoryAPI(noteId, clinicId);
      return { success: true };
    } catch (error: any) {
      console.error(
        "[VisitNotesService] Failed to clear manual edits history:",
        error
      );
      return {
        success: false,
        error: {
          message: error?.message || "Failed to clear manual edits history",
          code: error?.code,
        },
      };
    }
  };

  const getConsentTemplateMetadata = async (
    consentType: VisitNoteConsentType
  ): Promise<IGetConsentTemplateMetadataResponse> => {
    try {
      const data = await visitNotesRepository.getConsentTemplateMetadataAPI(
        consentType
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to load consent form",
          code: error?.code,
        },
      };
    }
  };

  const getHhRpmConsentDraftMetadata = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetHhRpmConsentDraftMetadataResponse> => {
    try {
      const data = await visitNotesRepository.getHhRpmConsentDraftMetadataAPI(
        clinicId,
        patientId
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to load editable consent form",
          code: error?.code,
        },
      };
    }
  };

  const getConsentTemplatePageImage = async (
    consentType: VisitNoteConsentType,
    page: number,
    dpi?: number
  ): Promise<IGetConsentTemplatePageResponse> => {
    try {
      const data = await visitNotesRepository.getConsentTemplatePageImageAPI(
        consentType,
        page,
        dpi
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || `Failed to load consent page ${page}`,
          code: error?.code,
        },
      };
    }
  };

  const submitHhRpmConsent = async (
    clinicId: number,
    patientId: number,
    request: IHhRpmConsentRequest
  ): Promise<ISubmitHhRpmConsentResponse> => {
    try {
      const data = await visitNotesRepository.submitHhRpmConsentAPI(
        clinicId,
        patientId,
        request
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to record Home Health RPM consent",
          code: error?.code,
          field_errors: error?.fieldErrors,
        },
      };
    }
  };

  const getHhRpmPatientConsentDraftMetadata = async (
    clinicId: number,
    patientId: number
  ): Promise<IGetHhRpmConsentDraftMetadataResponse> => {
    try {
      const data =
        await visitNotesRepository.getHhRpmPatientConsentDraftMetadataAPI(
          clinicId,
          patientId
        );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to load editable consent form",
          code: error?.code,
        },
      };
    }
  };

  const submitHhRpmPatientConsent = async (
    clinicId: number,
    patientId: number,
    request: IHhRpmPatientConsentRequest
  ): Promise<ISubmitHhRpmConsentResponse> => {
    try {
      const data = await visitNotesRepository.submitHhRpmPatientConsentAPI(
        clinicId,
        patientId,
        request
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message:
            error?.message ||
            "Failed to record Home Health RPM patient consent",
          code: error?.code,
          field_errors: error?.fieldErrors,
        },
      };
    }
  };

  const getPatientConsentHistory = async (
    clinicId: number,
    patientId: number,
    patientScope = false
  ): Promise<IGetPatientConsentHistoryResponse> => {
    try {
      const data = await visitNotesRepository.getPatientConsentHistoryAPI(
        clinicId,
        patientId,
        patientScope
      );
      return { success: true, data };
    } catch (error: any) {
      return {
        success: false,
        error: {
          message: error?.message || "Failed to load patient consent history",
          code: error?.code,
        },
      };
    }
  };

  return {
    createVisitNote,
    updateVisitNote,
    getAllVisitNotes,
    getAllVisitNotesFull,
    getScheduledVisitAssignments,
    getVisitNoteProviders,
    searchVisitNotes,
    getArchivedVisitNotes,
    restoreArchivedVisitNote,
    getVisitNote,
    getClinicianAcuityCatalog,
    putClinicianAssessment,
    listCustomAssessmentTemplates,
    getCustomAssessmentTemplate,
    createCustomAssessmentTemplate,
    updateCustomAssessmentTemplate,
    archiveCustomAssessmentTemplate,
    publishCustomAssessmentTemplate,
    getCustomAssessmentForms,
    putCustomAssessmentForm,
    deleteCustomAssessmentForm,
    refreshPastContextPrechart,
    getCarePlanPrechart,
    updateCarePlanPrechart,
    refreshCarePlanPrechart,
    deleteCarePlanPrechart,
    summarizeNarrative,
    sendEfax,
    downloadPartnershipSummaryDocx,
    getSidePeekProfile,
    generateSidePeekProfile,
    updateSidePeekProfile,
    getSafetyNote,
    updateSafetyNote,
    getCodeStatus,
    updateCodeStatus,
    getPatientContacts,
    updatePatientContacts,
    getPatientCaregivers,
    createPatientCaregiver,
    updatePatientCaregiver,
    deletePatientCaregiver,
    getPatientPrimaryCareProvider,
    createPatientPrimaryCareProvider,
    updatePatientPrimaryCareProvider,
    getPatientSpecialists,
    createPatientSpecialist,
    updatePatientSpecialist,
    deletePatientSpecialist,
    getPatientInsuranceCard,
    uploadPatientInsuranceCardImage,
    deletePatientInsuranceCardImage,
    getPatientExtraInfo,
    getPatientPrimaryLanguage,
    updatePatientPrimaryLanguage,
    updatePatientTimezone,
    getNextVisitPlan,
    updateNextVisitPlan,
    getPatientSummary,
    getPatientSummaryGenerationStatus,
    generatePatientSummary,
    getNoteLlmOutput,
    clearManualEditsHistory,
    getConsentTemplateMetadata,
    getHhRpmConsentDraftMetadata,
    getHhRpmPatientConsentDraftMetadata,
    getConsentTemplatePageImage,
    submitHhRpmConsent,
    submitHhRpmPatientConsent,
    getPatientConsentHistory,
  };
};
